/**
 * Supabase Client for React Frontend
 * ===================================
 * This module provides the Supabase client for authentication and data access.
 * 
 * IMPORTANT: Use environment variables for your Supabase credentials.
 * Create a .env file in react_frontend/ directory with:
 *   REACT_APP_SUPABASE_URL=your_supabase_url
 *   REACT_APP_SUPABASE_ANON_KEY=your_supabase_anon_key
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (process.env.NODE_ENV === 'development') {
  console.log('Supabase URL:', supabaseUrl);
  console.log('Supabase Key:', supabaseAnonKey ? 'Present' : 'Missing');
}

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables!');
  console.error('Please create a .env file in react_frontend/ with:');
  console.error('REACT_APP_SUPABASE_URL=your_supabase_url');
  console.error('REACT_APP_SUPABASE_ANON_KEY=your_supabase_anon_key');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

/**
 * Get the current session
 */
export const getSession = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error getting session:', error);
    return null;
  }
  return data.session;
};

/**
 * Get the current user
 */
export const getCurrentUser = async () => {
  const { data, error } = await supabase.auth.getUser();
  if (error) {
    console.error('Error getting user:', error);
    return null;
  }
  return data.user;
};

/**
 * Get the user's role by checking which table they belong to
 */
export const getUserRole = async (userId) => {
  if (!userId) return null;
  
  try {
    // Check admins table
    const { data: adminData, error: adminError } = await supabase
      .from('admins')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();  // Use maybeSingle instead of single
    
    if (adminData && !adminError) return 'admin';

    // Check vendors table
    const { data: vendorData, error: vendorError } = await supabase
      .from('vendors')
      .select('id, status')
      .eq('user_id', userId)
      .maybeSingle();  // Use maybeSingle instead of single
    
    if (vendorData && !vendorError) {
      // Check if vendor is approved
      if (vendorData.status === 'approved') {
        return 'vendor';
      } else {
        return 'vendor_pending'; // Vendor exists but not approved
      }
    }

    // Check customers table
    const { data: customerData, error: customerError } = await supabase
      .from('customers')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();  // Use maybeSingle instead of single
    
    if (customerData && !customerError) return 'customer';

    // User has no role assigned
    return null;
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
};

/**
 * Get user profile data based on their role
 */
export const getUserProfile = async (userId, role) => {
  if (!userId || !role) return null;
  
  try {
    let table;
    switch (role) {
      case 'admin':
        table = 'admins';
        break;
      case 'vendor':
      case 'vendor_pending':
        table = 'vendors';
        break;
      case 'customer':
        table = 'customers';
        break;
      default:
        return null;
    }

    const { data, error } = await supabase
      .from(table)
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();  // Use maybeSingle instead of single

    if (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
    return data;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
};

export default supabase;

